/**
 * What leaving a room costs, in words — and whether it costs anything at all.
 *
 * `DuelScreen.svelte` asks before it leaves a match that is live
 * (the shell's `BackGuard`), and the question has to be true. The facts are the
 * server's (`apps/server/src/match/duel-match.ts#leave`): a deliberate quit
 * while a round is counting in, playing or stopped ends the match — the other
 * seat wins it, or in co-op nobody does and the run is over for both. Between
 * two rounds the match is called off and the room goes back to its lobby
 * with nothing recorded. In the lobby, and once a match is over, leaving
 * costs nothing, and nothing is asked.
 *
 * Pure, so every case is unit tested (`test/nav/leave-question.test.ts`).
 */
import type { MatchPhase, MatchType } from '@interlock/protocol';

export interface LeaveContext {
  readonly role: 'player' | 'spectator';
  readonly phase: MatchPhase;
  /** True once the server has announced the match's end. */
  readonly matchEnded: boolean;
  /** True when the server refused this client: there is no seat to give up. */
  readonly refused: boolean;
}

/**
 * Whether leaving now would throw a match away — the condition the back
 * guard is armed on. A spectator has no seat, so it never is for one.
 */
export function matchIsLive(context: LeaveContext): boolean {
  if (context.role !== 'player') return false;
  if (context.matchEnded || context.refused) return false;
  return context.phase !== 'lobby' && context.phase !== 'matchOver';
}

export interface LeaveQuestionInput {
  readonly coop: boolean;
  readonly vsAi: boolean;
  readonly matchType: MatchType;
  readonly phase: MatchPhase;
  /** The other seat's display name, or `''` when nobody is sitting there. */
  readonly otherName: string;
}

export interface LeaveQuestion {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
}

export function leaveQuestion(input: LeaveQuestionInput): LeaveQuestion {
  const other = input.otherName.trim();

  if (input.coop) {
    return {
      title: 'Leave the run?',
      body:
        other === ''
          ? 'The run ends here. A shared well cannot be finished alone.'
          : `The run ends here for both of you. ${other} cannot finish a shared well alone.`,
      confirmLabel: 'Leave',
      cancelLabel: 'Keep playing',
    };
  }

  if (input.vsAi) {
    return {
      title: 'Leave the match?',
      body:
        input.phase === 'roundOver'
          ? 'The match against the computer is called off between rounds. Nothing is recorded.'
          : 'The match ends here and counts as a loss against the computer.',
      confirmLabel: 'Leave',
      cancelLabel: 'Keep playing',
    };
  }

  const who = other === '' ? 'Your opponent' : other;

  if (input.phase === 'roundOver') {
    return {
      title: 'Leave the match?',
      body: `The match is called off between rounds, with no result recorded. ${who} is left waiting in the room.`,
      confirmLabel: 'Leave',
      cancelLabel: 'Keep playing',
    };
  }

  return {
    title: 'Leave the match?',
    body:
      input.matchType === 'ranked'
        ? 'It counts as a loss.'
        : `It counts as a loss, and ${who} is left waiting in the room.`,
    confirmLabel: 'Leave',
    cancelLabel: 'Keep playing',
  };
}
